import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../api';
import { PageHeader, StatusBadge, Table, Pagination, EmptyState, TableSkeleton, UserCell, AstrologerCell } from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatCurrency, formatDate, formatDuration } from '../../utils/format';
import { MessageSquare, PhoneCall, Video, Filter, X, RefreshCw, CheckCircle, DollarSign, Eye } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

function typeLabel(t: string) {
  return t === 'call' ? 'Audio' : t;
}

export default function ConsultationsPage() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    setTypeFilter(searchParams.get('type') || '');
    setPage(1);
  }, [searchParams]);

  const { data, isLoading } = useQuery<{ data: any[]; pagination?: any }>({
    queryKey: ['admin-consultations', page, typeFilter, statusFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params: any = { page, limit: 20 };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const r = await api.get('/admin/consultations', { params });
      return r.data;
    },
    placeholderData: (prev) => prev,
  });

  const consultations = data?.data || [];
  const pagination = data?.pagination;

  const stats = [
    { label: 'Total', value: pagination?.total?.toLocaleString() ?? '—', icon: <MessageSquare size={14} /> },
    { label: 'Chat', value: consultations.filter((c) => c.type === 'chat').length, icon: <MessageSquare size={14} />, color: 'text-blue-600' },
    { label: 'Audio', value: consultations.filter((c) => c.type === 'call').length, icon: <PhoneCall size={14} />, color: 'text-green-600' },
    { label: 'Video', value: consultations.filter((c) => c.type === 'video').length, icon: <Video size={14} />, color: 'text-purple-600' },
    { label: 'Ended', value: consultations.filter((c) => c.status === 'ended').length, icon: <CheckCircle size={14} />, color: 'text-teal-600' },
    { label: 'Revenue', value: formatCurrency(consultations.reduce((s: number, c: any) => s + (c.totalAmount || 0), 0)), icon: <DollarSign size={14} />, color: 'text-primary-600' },
  ];

  const clearFilters = () => { setTypeFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); setPage(1); };
  const hasFilters = typeFilter || statusFilter || dateFrom || dateTo;

  const TYPE_ICON: Record<string, React.ReactNode> = { chat: <MessageSquare size={13} />, call: <PhoneCall size={13} />, video: <Video size={13} /> };

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' }, { key: 'type', label: 'Type' }, { key: 'status', label: 'Status' },
    { key: 'userId', label: 'User ID' }, { key: 'astrologerId', label: 'Astrologer ID' },
    { key: 'pricePerMinute', label: 'Rate (paise)' }, { key: 'duration', label: 'Duration (sec)' },
    { key: 'totalAmount', label: 'Amount (paise)' }, { key: 'createdAt', label: 'Date' },
  ];

  return (
    <div className="p-6">
      <PageHeader title="Consultations" subtitle="All chat, audio & video consultations">
        <ExportButton data={consultations} filename="consultations" columns={EXPORT_COLS} />
        <button onClick={() => qc.invalidateQueries({ queryKey: ['admin-consultations'] })} className="btn-secondary btn-sm"><RefreshCw size={14} /></button>
      </PageHeader>

      <OverviewBar items={stats} loading={isLoading} />

      {/* Filters */}
      <div className="card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1.5">
            {[['', 'All Types'], ['chat', 'Chat'], ['call', 'Audio'], ['video', 'Video']].map(([v, l]) => (
              <button key={v} onClick={() => { setTypeFilter(v); setPage(1); }} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${typeFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-1.5">
            {[['', 'All Status'], ['active', 'Active'], ['ended', 'Ended'], ['cancelled', 'Cancelled'], ['missed', 'Missed']].map(([v, l]) => (
              <button key={v} onClick={() => { setStatusFilter(v); setPage(1); }} className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${statusFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary btn-sm"><Filter size={14} /></button>
          {hasFilters && <button onClick={clearFilters} className="text-xs text-red-500 hover:underline flex items-center gap-1"><X size={12} />Clear</button>}
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">From Date</label><input type="date" className="input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">To Date</label><input type="date" className="input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={10} cols={7} /></div>
        ) : consultations.length === 0 ? (
          <EmptyState message="No consultations found" icon={<MessageSquare size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-th">#</th>
                <th className="table-th">User</th>
                <th className="table-th">Astrologer</th>
                <th className="table-th">Type</th>
                <th className="table-th">Duration</th>
                <th className="table-th">Amount</th>
                <th className="table-th">Status</th>
                <th className="table-th">Date</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {consultations.map((c: any, idx: number) => (
                <tr key={c._id} className="table-row">
                  <td className="table-td text-xs text-gray-400">{(page - 1) * 20 + idx + 1}</td>
                  <td className="table-td"><UserCell entity={c.userId} /></td>
                  <td className="table-td"><AstrologerCell entity={c.astrologerId} /></td>
                  <td className="table-td">
                    <span className="flex items-center gap-1.5 text-xs font-medium capitalize">
                      {TYPE_ICON[c.type]}{typeLabel(c.type)}
                    </span>
                  </td>
                  <td className="table-td text-xs">{c.duration ? formatDuration(c.duration) : '—'}</td>
                  <td className="table-td text-xs font-semibold">{c.totalAmount ? formatCurrency(c.totalAmount) : '—'}</td>
                  <td className="table-td"><StatusBadge status={c.status} /></td>
                  <td className="table-td text-xs">{formatDate(c.createdAt)}</td>
                  <td className="table-td">
                    <Link to={`/consultations/${c._id}`} className="btn-secondary btn-sm inline-flex items-center gap-1">
                      <Eye size={13} /> Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t border-gray-100">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={20} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
